import { useExecutionProperties } from 'src/modules/automations/hooks/executions/use-execution-properties';
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PiCheck, PiCopy } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { SectionSidebarMenu } from 'src/modules/shared/components/molecules/section-menu';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useEmployeeStepExecutions } from 'src/modules/shared/hooks/api/automations/employees/executions';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { AdminTenantPermission } from 'src/modules/shared/types/permissions.type';
import { ExecutionSummary } from 'src/modules/automations/components/organisms/employees/executions/execution-summary';
import { ExecutionChronology } from 'src/modules/automations/components/organisms/employees/executions/execution-chronology';
import { ExecutionTechnicalData } from 'src/modules/automations/components/organisms/employees/executions/execution-technical-data';
import { buildExecutionDetailPresentation } from 'src/modules/automations/utils/executions/execution-detail-presentation.util';
import { executionsSearchSchema } from 'src/modules/automations/schemas/employees/executions.schema';
import { Route as EmployeesEmployeeIdExecutionsIndexRoute } from 'src/routes/employees/$employeeId/executions/index';
import type { TCEmployeeStepExecutionListResponse } from 'src/api/generated';
import type { ExecutionDetailPresentation } from 'src/modules/automations/types/executions/execution-detail-presentation.type';

interface EmployeeExecutionPageProps {
  /** AI Agent identifier. */
  employeeId: string;
  /** Execution identifier. */
  executionId: string;
}

type ExecutionDetailTab = 'summary' | 'timeline' | 'technical';

const getExecutionHistoryTimestamp = (
  item: TCEmployeeStepExecutionListResponse[number]
): number =>
  new Date('createdAt' in item ? item.createdAt : item.occurredAt).getTime();

const isExecutionDetailTab = (value: string): value is ExecutionDetailTab =>
  value === 'summary' || value === 'timeline' || value === 'technical';

interface ExecutionDetailContentProps {
  /** Chronologically ordered execution steps. */
  steps: TCEmployeeStepExecutionListResponse;
  /** Business-facing execution projection. */
  presentation: ExecutionDetailPresentation;
  /** Whether the signed-in user may inspect raw technical data. */
  canViewTechnicalData: boolean;
}

const ExecutionTabContent: React.FC<{
  activeTab: ExecutionDetailTab;
  steps: TCEmployeeStepExecutionListResponse;
  presentation: ExecutionDetailPresentation;
  canViewTechnicalData: boolean;
}> = ({ activeTab, steps, presentation, canViewTechnicalData }) => {
  if (activeTab === 'summary') {
    return <ExecutionSummary presentation={presentation} />;
  }

  if (activeTab === 'timeline') {
    return <ExecutionChronology steps={steps} />;
  }

  return canViewTechnicalData ? <ExecutionTechnicalData steps={steps} /> : null;
};

const ExecutionDetailNavigation: React.FC<{
  activeTab: ExecutionDetailTab;
  canViewTechnicalData: boolean;
  onChange: (value: string) => void;
}> = ({ activeTab, canViewTechnicalData, onChange }) => {
  const { t } = useI18n();
  const items = [
    {
      id: 'summary',
      label: t('automations.aiAgents.detail.summary'),
    },
    {
      id: 'timeline',
      label: t('automations.aiAgents.detail.timeline'),
    },
    ...(canViewTechnicalData
      ? [
          {
            id: 'technical',
            label: t('automations.aiAgents.detail.technical'),
          },
        ]
      : []),
  ];

  return (
    <nav className="shrink-0 border-b border-slate-100 lg:w-72 lg:border-b-0 lg:border-r">
      <Panel variant="neutral" appearance="ghost" className="p-3">
        <SectionSidebarMenu
          items={items}
          activeId={activeTab}
          onSelect={onChange}
          variant="neutral"
          appearance="filled"
        />
      </Panel>
    </nav>
  );
};

const ExecutionDetailContent: React.FC<ExecutionDetailContentProps> = ({
  steps,
  presentation,
  canViewTechnicalData,
}) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<ExecutionDetailTab>('summary');
  const activeTabLabel =
    activeTab === 'summary'
      ? t('automations.aiAgents.detail.summary')
      : activeTab === 'timeline'
        ? t('automations.aiAgents.detail.timeline')
        : t('automations.aiAgents.detail.technical');
  const handleTabChange = (value: string) => {
    if (isExecutionDetailTab(value)) {
      setActiveTab(value);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <ExecutionDetailNavigation
        activeTab={activeTab}
        canViewTechnicalData={canViewTechnicalData}
        onChange={handleTabChange}
      />

      <div className="min-h-0 min-w-0 flex-1 overflow-auto bg-slate-50 p-4 sm:p-5">
        <div className="mx-auto w-full max-w-7xl">
          <div
            id={`tabpanel-${activeTab}`}
            role="tabpanel"
            aria-label={activeTabLabel}
          >
            <ExecutionTabContent
              activeTab={activeTab}
              steps={steps}
              presentation={presentation}
              canViewTechnicalData={canViewTechnicalData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Responsive execution detail with business, chronology, and support views. */
export const EmployeeExecutionPage: React.FC<EmployeeExecutionPageProps> = ({
  employeeId,
  executionId,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  const { hasPermission: canViewTechnicalData, loading: permissionsLoading } =
    useHasPermissions(AdminTenantPermission.Assume);
  const { stepExecutions, loading, error, refetch } =
    useEmployeeStepExecutions(executionId);
  const orderedSteps = useMemo(
    () =>
      [...stepExecutions].sort(
        (left, right) =>
          getExecutionHistoryTimestamp(left) -
          getExecutionHistoryTimestamp(right)
      ),
    [stepExecutions]
  );
  const properties = useExecutionProperties(orderedSteps);
  const presentation = buildExecutionDetailPresentation(
    orderedSteps,
    properties
  );

  const handleBack = () => {
    void navigate({
      to: EmployeesEmployeeIdExecutionsIndexRoute.to,
      params: { employeeId },
      search: (search) => executionsSearchSchema.parse(search),
    });
  };

  const copyAction = (
    <Tooltip
      size="xs"
      placement="top"
      content={
        isCopied
          ? t('automations.aiAgents.actions.copied')
          : t('automations.aiAgents.actions.copyId')
      }
    >
      <Button
        type="button"
        variant="secondary"
        appearance="outline"
        size="sm"
        onClick={() => void copyToClipboard(executionId)}
        className="w-full gap-2 sm:w-auto"
      >
        {isCopied ? (
          <PiCheck className="h-4 w-4 text-success-600" />
        ) : (
          <PiCopy className="h-4 w-4" />
        )}
        <span className="max-w-40 truncate font-mono text-xs sm:max-w-56">
          {executionId}
        </span>
      </Button>
    </Tooltip>
  );

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={t('automations.aiAgents.detail.title')}
          subtitle={t('automations.aiAgents.detail.description')}
          onBack={handleBack}
          backLabel={t('automations.aiAgents.detail.back')}
          actions={copyAction}
        />

        {loading ? (
          <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50">
            <Loading />
          </div>
        ) : error ? (
          <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50 p-4">
            <ErrorState
              title={t('automations.aiAgents.detail.errorTitle')}
              description={t('automations.aiAgents.detail.errorDescription')}
              onAction={() => void refetch()}
              actionContent={t('automations.aiAgents.actions.retry')}
            />
          </div>
        ) : (
          <ExecutionDetailContent
            steps={orderedSteps}
            presentation={presentation}
            canViewTechnicalData={!permissionsLoading && canViewTechnicalData}
          />
        )}
      </Panel>
    </div>
  );
};
